/* eslint-disable no-mixed-spaces-and-tabs */
import Cookies from 'js-cookie';
import { secureCookieOptions } from '@/utils/secureCookieOptions';
import { useMSALHelper } from './useMSALHelper';
import { msGraphAxios, scopes } from '@/utils/msGraphUtils';
import {
	useAddFileToSharepointMutation,
	useBatchAddFilesToSharepointMutation,
} from '@/features/msGraph/msGraphApi';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { specialChars, specialCharsGlobal, replaceFolderName } from '@/utils/constants';
import { createUniqueFileName } from '@/utils/folders';
import { useCallback } from 'react';
import { graphClient } from '@/utils/msGraphClient';
import { getSharepointError, UPLOAD_SHAREPOINT_ERROR } from '@/features/msGraph/helper';

// Custom hook to interact with Microsoft graph
export const useMsGraph = (userId) => {
	const dispatch = useDispatch();
	const [addFileToSharepoint] = useAddFileToSharepointMutation();
	const [batchAddFilesToSharepoint] = useBatchAddFilesToSharepointMutation();

	const endpointPrefix = userId ? `users/${userId}` : '/me';

	const { getAccessToken } = useMSALHelper();

	const getGraphAccessToken = async () => {
		const token = await getAccessToken(scopes);
		Cookies.set('token', token, secureCookieOptions);
		return token;
	};

	const renameFile = ({ file, maxLength, createUnique = false }) => {
		const end = file.name.split('.');
		const type = end[end.length - 1];
		const name = file.name.slice(0, -`.${type}`.length);
		let cleanedName = name.trim().replace(specialCharsGlobal, '').replace('.', '_').trim();
		if (maxLength) {
			cleanedName = cleanedName.slice(0, maxLength).trim();
		}
		const fileName = createUnique
			? createUniqueFileName(`${cleanedName}.${type}`)
			: `${cleanedName}.${type}`;
		const newFile = new File([file], fileName, {
			type: file.type,
			lastModified: file.lastModified,
		});
		return newFile;
	};

	const isPathTooLongError = (message = '') =>
		String(message).toLowerCase().includes('path is too long');

	const showUploadErrorToast = (error) => {
		console.error('[useMsGraph] Upload error', error);
		const sharepointError = getSharepointError(error, UPLOAD_SHAREPOINT_ERROR);
		if (!sharepointError) return;

		dispatch(
			showToast({
				id: `sharepoint-upload-error:${sharepointError.errorMessage}`,
				message: sharepointError.errorMessage,
				error: true,
				autohide: false,
			}),
		);
	};

	const uploadFileAndRetry = async ({
		driveId,
		itemId,
		file: originalFile,
		metadata = {},
		silent = false,
	}) => {
		const file = renameFile({ file: originalFile });
		return await addFileToSharepoint({
			driveId,
			itemId,
			file,
			metadata,
		})
			.unwrap()
			.then((res) => {
				console.log('[uploadFileAndRetry] addFileToSharepoint RES', res);
				const nameChange = res?.name !== originalFile.name;
				if (!silent) {
					const message = nameChange
						? `Successfully uploaded but renamed to '${res?.name}' due to special character issues.`
						: `Successfully uploaded '${res?.name}'.`;
					dispatch(
						showToast({
							message,
							duration: 10000,
							action: {
								label: 'Rename file',
								renameFile: true,
								warning: nameChange,
								file: res,
							},
						}),
					);
				}
				return res;
			})
			.catch(async (e) => {
				console.error('[uploadFileAndRetry] Error uploading file via Graph API', file, e);
				if (isPathTooLongError(e?.message)) {
					const newFile = renameFile({ file, maxLength: 50, createUnique: true });
					return await addFileToSharepoint({
						driveId,
						itemId,
						file: newFile,
						metadata,
					})
						.unwrap()
						.then((res) => {
							console.log('[uploadFileAndRetry] RES', res);
							if (!silent) {
								dispatch(
									showToast({
										message: `Successfully uploaded but renamed to '${newFile.name}' due to file path length issues.`,
										duration: 10000,
										action: {
											label: 'Rename file',
											renameFile: true,
											warning: true,
											file: res,
										},
									}),
								);
							}
							return res;
						})
						.catch(async (err) => {
							if (!silent) {
								showUploadErrorToast(err);
							}
							return null;
						});
				}

				if (!silent) {
					showUploadErrorToast(e);
				}
				return null;
			});
	};

	const uploadFilesAndRetry = useCallback(
		async ({ driveId, itemId, files = [], metadata = {}, silent = false }) => {
			if (files.length === 0) {
				return {
					success: true,
					uploadedFiles: [],
					failedFiles: [],
					uploadedCount: 0,
					failedCount: 0,
				};
			}

			if (files.length === 1) {
				const originalFile = files[0]?.file ?? files[0];
				const fileMetadata = files[0]?.metadata ?? metadata;
				const uploaded = await uploadFileAndRetry({
					driveId,
					itemId,
					file: originalFile,
					metadata: fileMetadata,
					silent,
				});

				return {
					success: Boolean(uploaded),
					uploadedFiles: uploaded ? [uploaded] : [],
					failedFiles: uploaded ? [] : [{ name: originalFile.name }],
					uploadedCount: uploaded ? 1 : 0,
					failedCount: uploaded ? 0 : 1,
					cachePatched: uploaded?.cachePatched ?? false,
				};
			}

			const preparedEntries = files.map((entry) => {
				const originalFile = entry?.file ?? entry;
				return {
					originalFile,
					file: renameFile({ file: originalFile }),
					metadata: entry?.metadata ?? metadata,
				};
			});

			let uploadedFiles = [];
			let failedFiles = [];
			let batchCachePatched = false;

			try {
				const result = await batchAddFilesToSharepoint({
					driveId,
					itemId,
					files: preparedEntries.map(({ file, metadata: fileMetadata }) => ({
						file,
						metadata: fileMetadata,
					})),
				}).unwrap();

				uploadedFiles = result?.uploadedFiles ?? [];
				failedFiles = [...(result?.failedFiles ?? [])];
				batchCachePatched = result?.cachePatched ?? false;
			} catch (error) {
				console.error('[uploadFilesAndRetry] Batch upload failed', error);
				if (!silent) {
					showUploadErrorToast(error);
				}

				return {
					success: false,
					uploadedFiles: [],
					failedFiles: preparedEntries.map(({ originalFile }) => ({
						name: originalFile.name,
						error: error?.message,
					})),
					uploadedCount: 0,
					failedCount: preparedEntries.length,
				};
			}

			const pathTooLongFailures = failedFiles.filter((failure) =>
				isPathTooLongError(failure.error?.message ?? failure.error),
			);

			if (pathTooLongFailures.length > 0) {
				const retriedNames = new Set();

				for (const failure of pathTooLongFailures) {
					const entry = preparedEntries.find(
						({ file, originalFile }) =>
							file.name === failure.name || originalFile.name === failure.name,
					);

					if (!entry || retriedNames.has(entry.originalFile.name)) continue;

					retriedNames.add(entry.originalFile.name);
					const uploaded = await uploadFileAndRetry({
						driveId,
						itemId,
						file: entry.originalFile,
						metadata: entry.metadata,
						silent: true,
					});

					failedFiles = failedFiles.filter(
						(f) => f.name !== failure.name && f.name !== entry.file.name,
					);

					if (uploaded) {
						uploadedFiles.push(uploaded);
					} else {
						failedFiles.push({
							name: entry.originalFile.name,
							error: 'Failed to upload after path length retry',
						});
					}
				}
			}

			const renamedCount = uploadedFiles.filter((uploaded) => {
				const entry = preparedEntries.find(
					({ file, originalFile }) =>
						file.name === uploaded?.name || originalFile.name === uploaded?.name,
				);
				return entry && uploaded?.name !== entry.originalFile.name;
			}).length;

			const uploadedCount = uploadedFiles.length;
			const failedCount = failedFiles.length;
			const totalCount = preparedEntries.length;

			if (!silent) {
				if (failedCount === 0) {
					const message =
						renamedCount > 0
							? `Successfully uploaded ${uploadedCount} file${uploadedCount === 1 ? '' : 's'}. ${renamedCount} renamed due to special characters.`
							: `Successfully uploaded ${uploadedCount} file${uploadedCount === 1 ? '' : 's'}.`;

					dispatch(
						showToast({
							message,
							duration: 10000,
						}),
					);
				} else if (uploadedCount > 0) {
					const firstFailure = failedFiles[0];
					const detail = firstFailure
						? ` ${firstFailure.name}: ${getSharepointError(firstFailure.error, UPLOAD_SHAREPOINT_ERROR)?.errorMessage ?? ''}`
						: '';
					dispatch(
						showToast({
							id: `sharepoint-upload-error:${detail}`,
							message: `Uploaded ${uploadedCount} of ${totalCount} files. ${failedCount} failed.${detail}`,
							duration: 10000,
							error: true,
							autohide: false,
						}),
					);
				} else {
					showUploadErrorToast(failedFiles[0]?.error);
				}
			}

			return {
				success: failedCount === 0,
				uploadedFiles,
				failedFiles,
				uploadedCount,
				failedCount,
				cachePatched: batchCachePatched,
			};
		},
		[batchAddFilesToSharepoint, dispatch],
	);

	// Upload the sent email as an .eml file to the selected SharePoint folder
	const uploadEmail = useCallback(
		async ({ messageId, sendResult, driveId, selectedFolder, subject, metadata = {} }) => {
			const graphMessageId = sendResult?.messageId ?? messageId;
			const messagePath = sendResult?.graphUserId
				? `/users/${sendResult.graphUserId}/messages/${graphMessageId}/$value`
				: `/me/messages/${graphMessageId}/$value`;
			const result = { success: true };
			try {
				const content = await graphClient
					.api(messagePath)
					.header('Prefer', 'IdType="ImmutableId"')
					.responseType('blob')
					.get();
				const fileName = `${replaceFolderName(subject)}.eml`;
				const file = new File([content], fileName, {
					type: 'message/rfc822',
					lastModified: Date.now(),
				});
				result.success = Boolean(
					await uploadFileAndRetry({
						driveId,
						itemId: selectedFolder,
						file,
						metadata,
					}),
				);
			} catch (error) {
				console.log('🙅 ~ Error uploading sent email to SharePoint', error);
				result.success = false;
			}

			return result;
		},
		[uploadFileAndRetry],
	);

	// Returning the functions to be used elsewhere in the app
	return {
		getGraphAccessToken,
		uploadFileAndRetry,
		uploadFilesAndRetry,
		uploadEmail,
	};
};
